import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth, useProfile } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Loader2, CheckCircle, MessageCircle, LogOut, ShieldAlert, ShieldCheck, ShieldOff, Users, ArrowLeft, Activity, Flag, Clock, ArrowUpDown, UserX, Eye, EyeOff, Plus, Bot, Trash2, CalendarClock } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { toastError } from "@/lib/errors";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";

const SUPER_ADMIN_EMAIL = "coach@ivanrissopatron.com";

type PendingUser = {
  id: string;
  display_name: string;
  whatsapp: string | null;
  email: string;
  created_at: string;
};

type ApprovedUser = {
  id: string;
  display_name: string;
  email: string;
  is_admin: boolean;
  created_at: string;
};

type UserActivity = {
  id: string;
  display_name: string;
  email: string;
  last_seen_at: string | null;
  days_active_30d: number;
  minutes_active_30d: number;
};

type Contribution = {
  id: string;
  user_id: string;
  display_name: string;
  type: string;
  description: string;
  status: string;
  admin_notes: string | null;
  created_at: string;
};

type BotUser = {
  user_id: string;
  display_name: string;
  email: string;
  telegram_enabled: boolean | null;
  whatsapp_enabled: boolean | null;
  added_at: string | null;
};

type BotStat = {
  user_id: string;
  display_name: string;
  email: string;
  messages_today: number;
  messages_this_month: number;
  tokens_input_month: number;
  tokens_output_month: number;
  estimated_cost_usd: number;
  daily_limit: number | null;
  monthly_token_limit: number | null;
  notes: string | null;
};

function fmtTokens(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
}

const CONTRIBUTION_TYPE_LABELS: Record<string, string> = {
  error_texto: "Error de texto",
  error_funcionalidad: "Error de funcionalidad",
  sugerencia: "Sugerencia",
  otro: "Otro",
};

const STATUS_LABELS: Record<string, { label: string; next: string; nextLabel: string }> = {
  pendiente:    { label: "Pendiente",    next: "aceptado",    nextLabel: "Aceptar" },
  aceptado:     { label: "Aceptado",     next: "recompensado", nextLabel: "Recompensar" },
  recompensado: { label: "Recompensado", next: "pendiente",   nextLabel: "Resetear" },
};

export const Route = createFileRoute("/admin")({
  component: AdminPage,
});

function AdminPage() {
  const { user, loading: authLoading } = useAuth();
  const { profile, loading: profileLoading } = useProfile();
  const navigate = useNavigate();

  const loading = authLoading || profileLoading;

  const isSuperAdmin = user?.email === SUPER_ADMIN_EMAIL;
  const isAdmin = isSuperAdmin || profile?.is_admin === true;

  // --- Pendientes ---
  const [pendingUsers, setPendingUsers] = useState<PendingUser[]>([]);
  const [fetchingPending, setFetchingPending] = useState(true);
  const [approvingId, setApprovingId] = useState<string | null>(null);
  const [rejectingId, setRejectingId] = useState<string | null>(null);
  const [userToReject, setUserToReject] = useState<PendingUser | null>(null);

  // --- Modo Admin ---
  const [approvedUsers, setApprovedUsers] = useState<ApprovedUser[]>([]);
  const [fetchingApproved, setFetchingApproved] = useState(false);
  const [togglingId, setTogglingId] = useState<string | null>(null);

  // --- Actividad ---
  const [activityData, setActivityData] = useState<UserActivity[]>([]);
  const [fetchingActivity, setFetchingActivity] = useState(false);

  // --- Aportes ---
  const [contributions, setContributions] = useState<Contribution[]>([]);
  const [fetchingContributions, setFetchingContributions] = useState(false);
  const [updatingContributionId, setUpdatingContributionId] = useState<string | null>(null);

  // --- Controles de actividad ---
  const [sortField, setSortField] = useState<"minutes" | "lastSeen">("minutes");
  const [sortOrder, setSortOrder] = useState<"most" | "least">("most");
  const [showInactiveOnly, setShowInactiveOnly] = useState(false);
  const [hideEmails, setHideEmails] = useState(false);

  // --- Bot ---
  const [botUsers, setBotUsers] = useState<BotUser[]>([]);
  const [fetchingBotUsers, setFetchingBotUsers] = useState(false);
  const [togglingBotAccess, setTogglingBotAccess] = useState<string | null>(null);
  const [botStats, setBotStats] = useState<BotStat[]>([]);
  const [fetchingBotStats, setFetchingBotStats] = useState(false);
  const [editingQuotaId, setEditingQuotaId] = useState<string | null>(null);
  const [quotaDailyLimit, setQuotaDailyLimit] = useState("");
  const [quotaMonthlyLimit, setQuotaMonthlyLimit] = useState("");
  const [quotaNotes, setQuotaNotes] = useState("");
  const [savingQuota, setSavingQuota] = useState(false);

  // --- Formulario de aporte manual ---
  const [showAddForm, setShowAddForm] = useState(false);
  const [newUserId, setNewUserId] = useState("");
  const [newType, setNewType] = useState("");
  const [newDesc, setNewDesc] = useState("");
  const [savingAdd, setSavingAdd] = useState(false);

  const fetchPending = useCallback(async () => {
    setFetchingPending(true);
    const { data, error } = await supabase.rpc("get_pending_users");
    if (error) { toastError(error); setFetchingPending(false); return; }
    setPendingUsers((data as PendingUser[]) ?? []);
    setFetchingPending(false);
  }, []);

  const fetchApproved = useCallback(async () => {
    setFetchingApproved(true);
    const { data, error } = await supabase.rpc("get_approved_users");
    if (error) { toastError(error); setFetchingApproved(false); return; }
    setApprovedUsers((data as ApprovedUser[]) ?? []);
    setFetchingApproved(false);
  }, []);

  const fetchActivity = useCallback(async () => {
    setFetchingActivity(true);
    const { data, error } = await supabase.rpc("get_users_activity");
    if (error) { toastError(error); setFetchingActivity(false); return; }
    setActivityData((data as UserActivity[]) ?? []);
    setFetchingActivity(false);
  }, []);

  const fetchContributions = useCallback(async () => {
    setFetchingContributions(true);
    const { data, error } = await supabase.rpc("get_all_contributions");
    if (error) { toastError(error); setFetchingContributions(false); return; }
    setContributions((data as Contribution[]) ?? []);
    setFetchingContributions(false);
  }, []);

  const fetchBotUsers = useCallback(async () => {
    setFetchingBotUsers(true);
    const { data, error } = await supabase.rpc("admin_get_bot_users");
    if (error) { toastError(error); setFetchingBotUsers(false); return; }
    setBotUsers((data as BotUser[]) ?? []);
    setFetchingBotUsers(false);
  }, []);

  const fetchBotStats = useCallback(async () => {
    setFetchingBotStats(true);
    const { data, error } = await supabase.rpc("admin_get_bot_stats");
    if (error) { toastError(error); setFetchingBotStats(false); return; }
    setBotStats((data as BotStat[]) ?? []);
    setFetchingBotStats(false);
  }, []);

  async function handleUpdateContribution(id: string, nextStatus: string) {
    setUpdatingContributionId(id);
    const { error } = await supabase.rpc("update_contribution_status", {
      _contribution_id: id,
      _status: nextStatus,
    });
    setUpdatingContributionId(null);
    if (error) { toastError(error); return; }
    setContributions((prev) =>
      prev.map((c) => c.id === id ? { ...c, status: nextStatus } : c)
    );
  }

  async function handleAdminAddContribution() {
    if (!newUserId || !newType || !newDesc.trim()) return;
    setSavingAdd(true);
    const { error } = await supabase.rpc("admin_add_contribution", {
      _user_id: newUserId,
      _type: newType,
      _description: newDesc.trim(),
    });
    setSavingAdd(false);
    if (error) { toastError(error); return; }
    toast.success("Aporte cargado");
    setNewUserId(""); setNewType(""); setNewDesc("");
    setShowAddForm(false);
    fetchContributions();
  }

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate({ to: "/auth" }); return; }
    if (!isAdmin) return;
    fetchPending();
  }, [user, loading, isAdmin, navigate, fetchPending]);

  async function handleApprove(userId: string, userName: string) {
    setApprovingId(userId);
    const { error } = await supabase.rpc("approve_user", { _user_id: userId });
    setApprovingId(null);
    if (error) { toastError(error); return; }
    toast.success(`${userName} aprobado`);
    setPendingUsers((prev) => prev.filter((u) => u.id !== userId));
  }

  async function handleReject(userId: string, userName: string) {
    setRejectingId(userId);
    const { error } = await supabase.rpc("reject_pending_user", { _user_id: userId });
    setRejectingId(null);
    setUserToReject(null);
    if (error) { toastError(error); return; }
    toast.success(`${userName} rechazado y eliminado`);
    setPendingUsers((prev) => prev.filter((u) => u.id !== userId));
  }

  async function handleToggleAdmin(userId: string, userName: string, currentIsAdmin: boolean) {
    setTogglingId(userId);
    const { error } = await supabase.rpc("set_user_admin", {
      _user_id: userId,
      _is_admin: !currentIsAdmin,
    });
    setTogglingId(null);
    if (error) { toastError(error); return; }
    const action = currentIsAdmin ? "removido de" : "agregado a";
    toast.success(`${userName} ${action} los admins`);
    setApprovedUsers((prev) =>
      prev.map((u) => u.id === userId ? { ...u, is_admin: !currentIsAdmin } : u)
    );
  }

  async function handleToggleBotAccess(
    userId: string,
    displayName: string,
    channel: string,
    currentEnabled: boolean | null,
  ) {
    const newEnabled = !currentEnabled;
    setTogglingBotAccess(`${userId}-${channel}`);
    const { error } = await supabase.rpc("admin_toggle_bot_access", {
      p_user_id: userId,
      p_channel: channel,
      p_enabled: newEnabled,
    });
    setTogglingBotAccess(null);
    if (error) { toastError(error); return; }
    toast.success(`${displayName} ${newEnabled ? "habilitado" : "deshabilitado"} en ${channel}`);
    setBotUsers((prev) =>
      prev.map((u) =>
        u.user_id === userId
          ? {
              ...u,
              telegram_enabled: channel === "telegram" ? newEnabled : u.telegram_enabled,
              whatsapp_enabled: channel === "whatsapp" ? newEnabled : u.whatsapp_enabled,
            }
          : u,
      ),
    );
  }

  async function handleSetQuota(userId: string) {
    setSavingQuota(true);
    const { error } = await supabase.rpc("admin_set_bot_quota", {
      p_user_id: userId,
      p_daily_message_limit: quotaDailyLimit ? parseInt(quotaDailyLimit) : null,
      p_monthly_token_limit: quotaMonthlyLimit ? parseInt(quotaMonthlyLimit) : null,
      p_notes: quotaNotes.trim() || null,
    });
    setSavingQuota(false);
    if (error) { toastError(error); return; }
    toast.success("Cuota guardada");
    setEditingQuotaId(null);
    fetchBotStats();
  }

  async function handleSignOut() {
    await supabase.auth.signOut();
    navigate({ to: "/auth" });
  }

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-muted/30 p-4">
        <Card className="w-full max-w-sm text-center">
          <CardContent className="pt-8 pb-6 space-y-3">
            <ShieldAlert className="mx-auto h-10 w-10 text-destructive" />
            <p className="font-medium">Acceso no autorizado</p>
            <Button variant="outline" onClick={() => navigate({ to: "/dashboard" })}>Volver al inicio</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const displayedActivity = [...activityData]
    .filter((u) => showInactiveOnly ? u.days_active_30d === 0 : true)
    .sort((a, b) => {
      const dir = sortOrder === "most" ? -1 : 1; // "most" = descendente
      if (sortField === "lastSeen") {
        const av = a.last_seen_at ? new Date(a.last_seen_at).getTime() : 0;
        const bv = b.last_seen_at ? new Date(b.last_seen_at).getTime() : 0;
        return (av - bv) * dir;
      }
      return (a.minutes_active_30d - b.minutes_active_30d) * dir;
    });

  return (
    <div className="min-h-screen bg-muted/30 p-4">
      <div className="mx-auto max-w-2xl space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Panel de administración</h1>
            <p className="text-sm text-muted-foreground">
              {isSuperAdmin ? "Super admin" : "Admin"}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => navigate({ to: "/dashboard" })}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Volver
            </Button>
            <Button variant="ghost" size="sm" onClick={handleSignOut}>
              <LogOut className="mr-2 h-4 w-4" />
              Salir
            </Button>
          </div>
        </div>

        <Tabs defaultValue="pending">
          <TabsList className={isSuperAdmin ? "grid w-full grid-cols-5" : isAdmin ? "grid w-full grid-cols-3" : ""}>
            <TabsTrigger value="pending">Pendientes</TabsTrigger>
            {isAdmin && (
              <TabsTrigger value="activity" onClick={fetchActivity}>Actividad</TabsTrigger>
            )}
            {isAdmin && (
              <TabsTrigger value="contributions" onClick={fetchContributions}>Aportes</TabsTrigger>
            )}
            {isSuperAdmin && (
              <TabsTrigger value="admins" onClick={fetchApproved}>Modo Admin</TabsTrigger>
            )}
            {isSuperAdmin && (
              <TabsTrigger value="bot" onClick={() => { fetchBotUsers(); fetchBotStats(); }}>Bot</TabsTrigger>
            )}
          </TabsList>

          {/* ── Pestaña Pendientes ── */}
          <TabsContent value="pending" className="mt-4 space-y-3">
            {fetchingPending ? (
              <div className="flex justify-center py-12">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : pendingUsers.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <CheckCircle className="mx-auto mb-3 h-10 w-10 text-emerald-500" />
                  <p className="font-medium">Sin pendientes</p>
                  <p className="text-sm text-muted-foreground">Todos los usuarios están aprobados.</p>
                </CardContent>
              </Card>
            ) : (
              <>
                <p className="text-sm text-muted-foreground">
                  {pendingUsers.length} usuario{pendingUsers.length !== 1 ? "s" : ""} esperando aprobación
                </p>
                {pendingUsers.map((u) => (
                  <Card key={u.id}>
                    <CardHeader className="pb-2">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <CardTitle className="text-base truncate">{u.display_name}</CardTitle>
                          <CardDescription className="truncate">{u.email}</CardDescription>
                        </div>
                        <Badge variant="secondary" className="shrink-0 text-xs">
                          {new Date(u.created_at).toLocaleDateString("es-AR", { day: "2-digit", month: "short" })}
                        </Badge>
                      </div>
                    </CardHeader>
                    <CardContent className="flex items-center justify-between gap-3">
                      <div className="text-sm text-muted-foreground">
                        {u.whatsapp ? (
                          <a
                            href={`https://wa.me/${u.whatsapp.replace(/\D/g, "")}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-1.5 text-emerald-600 hover:underline"
                          >
                            <MessageCircle className="h-4 w-4" />
                            {u.whatsapp}
                          </a>
                        ) : (
                          <span className="italic">Sin WhatsApp</span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-destructive hover:text-destructive"
                          onClick={() => setUserToReject(u)}
                          disabled={rejectingId === u.id || approvingId === u.id}
                        >
                          {rejectingId === u.id
                            ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            : <Trash2 className="mr-2 h-4 w-4" />
                          }
                          Rechazar
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => handleApprove(u.id, u.display_name)}
                          disabled={approvingId === u.id || rejectingId === u.id}
                        >
                          {approvingId === u.id
                            ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            : <CheckCircle className="mr-2 h-4 w-4" />
                          }
                          Aprobar
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </>
            )}
          </TabsContent>

          {/* ── Pestaña Actividad ── */}
          {isAdmin && (
            <TabsContent value="activity" className="mt-4 space-y-3">
              {fetchingActivity ? (
                <div className="flex justify-center py-12">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : activityData.length === 0 ? (
                <Card>
                  <CardContent className="py-12 text-center">
                    <Activity className="mx-auto mb-3 h-10 w-10 text-muted-foreground" />
                    <p className="font-medium">Sin datos aún</p>
                    <p className="text-sm text-muted-foreground">Los datos aparecen a medida que los usuarios usen la app.</p>
                  </CardContent>
                </Card>
              ) : (
                <>
                  <div className="flex flex-wrap items-center gap-2">
                    <Button
                      size="sm" variant="outline"
                      onClick={() => setSortField((f) => f === "minutes" ? "lastSeen" : "minutes")}
                      className="text-xs gap-1.5"
                    >
                      {sortField === "minutes"
                        ? <><Clock className="h-3 w-3" />Por actividad</>
                        : <><CalendarClock className="h-3 w-3" />Por última conexión</>}
                    </Button>
                    <Button
                      size="sm" variant="outline"
                      onClick={() => setSortOrder((s) => s === "most" ? "least" : "most")}
                      className="text-xs gap-1.5"
                    >
                      <ArrowUpDown className="h-3 w-3" />
                      {sortField === "minutes"
                        ? (sortOrder === "most" ? "Más activos primero" : "Menos activos primero")
                        : (sortOrder === "most" ? "Más recientes primero" : "Más antiguos primero")}
                    </Button>
                    <Button
                      size="sm" variant={showInactiveOnly ? "default" : "outline"}
                      onClick={() => setShowInactiveOnly((v) => !v)}
                      className="text-xs gap-1.5"
                    >
                      <UserX className="h-3 w-3" />
                      Solo inactivos
                    </Button>
                    <Button
                      size="sm" variant={hideEmails ? "default" : "outline"}
                      onClick={() => setHideEmails((v) => !v)}
                      className="text-xs gap-1.5"
                    >
                      {hideEmails ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
                      {hideEmails ? "Emails ocultos" : "Ocultar emails"}
                    </Button>
                    <span className="text-xs text-muted-foreground ml-auto">
                      {displayedActivity.length} de {activityData.length} usuario{activityData.length !== 1 ? "s" : ""}
                    </span>
                  </div>
                  {displayedActivity.length === 0 ? (
                    <Card>
                      <CardContent className="py-8 text-center">
                        <p className="text-sm text-muted-foreground">No hay usuarios inactivos.</p>
                      </CardContent>
                    </Card>
                  ) : (
                    displayedActivity.map((u) => (
                      <Card key={u.id}>
                        <CardContent className="py-3">
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              <p className="font-medium text-sm truncate">{u.display_name}</p>
                              {!hideEmails && <p className="text-xs text-muted-foreground truncate">{u.email}</p>}
                            </div>
                            <div className="shrink-0 text-right space-y-0.5">
                              <div className="flex items-center justify-end gap-1 text-xs text-muted-foreground">
                                <Clock className="h-3 w-3" />
                                {u.minutes_active_30d >= 60
                                  ? `${Math.round(u.minutes_active_30d / 60)}h aprox.`
                                  : `${u.minutes_active_30d} min`}
                              </div>
                              <p className="text-xs text-muted-foreground">{u.days_active_30d} día{u.days_active_30d !== 1 ? "s" : ""} activo</p>
                            </div>
                          </div>
                          <p className="text-xs text-muted-foreground mt-1.5">
                            {u.last_seen_at
                              ? `Última vez: ${new Date(u.last_seen_at).toLocaleDateString("es-AR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}`
                              : "Nunca accedió"}
                          </p>
                        </CardContent>
                      </Card>
                    ))
                  )}
                </>
              )}
            </TabsContent>
          )}

          {/* ── Pestaña Aportes ── */}
          {isAdmin && (
            <TabsContent value="contributions" className="mt-4 space-y-3">
              <div className="flex justify-end">
                <Button
                  size="sm"
                  className="gap-1.5"
                  onClick={() => {
                    if (activityData.length === 0) fetchActivity();
                    setShowAddForm((v) => !v);
                  }}
                >
                  <Plus className="h-4 w-4" />
                  Cargar aporte
                </Button>
              </div>

              {showAddForm && (
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm">Cargar aporte manual</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="space-y-1.5">
                      <Label className="text-xs">Usuario</Label>
                      <Select value={newUserId} onValueChange={setNewUserId}>
                        <SelectTrigger className="h-8 text-sm">
                          <SelectValue placeholder="Seleccioná..." />
                        </SelectTrigger>
                        <SelectContent>
                          {activityData.map((u) => (
                            <SelectItem key={u.id} value={u.id}>{u.display_name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs">Tipo</Label>
                      <Select value={newType} onValueChange={setNewType}>
                        <SelectTrigger className="h-8 text-sm">
                          <SelectValue placeholder="Seleccioná..." />
                        </SelectTrigger>
                        <SelectContent>
                          {Object.entries(CONTRIBUTION_TYPE_LABELS).map(([v, l]) => (
                            <SelectItem key={v} value={v}>{l}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs">Descripción</Label>
                      <Textarea
                        value={newDesc}
                        onChange={(e) => setNewDesc(e.target.value)}
                        placeholder="Describí el aporte..."
                        rows={3}
                        className="text-sm"
                      />
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm" className="flex-1"
                        onClick={handleAdminAddContribution}
                        disabled={savingAdd || !newUserId || !newType || !newDesc.trim()}
                      >
                        {savingAdd && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                        Guardar
                      </Button>
                      <Button
                        size="sm" variant="outline"
                        onClick={() => { setShowAddForm(false); setNewUserId(""); setNewType(""); setNewDesc(""); }}
                      >
                        Cancelar
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}

              {fetchingContributions ? (
                <div className="flex justify-center py-12">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : contributions.length === 0 ? (
                <Card>
                  <CardContent className="py-12 text-center">
                    <Flag className="mx-auto mb-3 h-10 w-10 text-muted-foreground" />
                    <p className="font-medium">Sin aportes aún</p>
                    <p className="text-sm text-muted-foreground">Cuando un usuario reporte algo aparecerá aquí.</p>
                  </CardContent>
                </Card>
              ) : (
                <>
                  <p className="text-sm text-muted-foreground">{contributions.length} aporte{contributions.length !== 1 ? "s" : ""} recibido{contributions.length !== 1 ? "s" : ""}</p>
                  {contributions.map((c) => {
                    const s = STATUS_LABELS[c.status] ?? STATUS_LABELS.pendiente;
                    return (
                      <Card key={c.id}>
                        <CardHeader className="pb-2">
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              <CardTitle className="text-sm truncate">{c.display_name}</CardTitle>
                              <CardDescription className="text-xs">{CONTRIBUTION_TYPE_LABELS[c.type] ?? c.type}</CardDescription>
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                              <Badge
                                variant={c.status === "recompensado" ? "default" : c.status === "aceptado" ? "secondary" : "outline"}
                                className="text-xs"
                              >
                                {s.label}
                              </Badge>
                              <Badge variant="outline" className="text-xs">
                                {new Date(c.created_at).toLocaleDateString("es-AR", { day: "2-digit", month: "short" })}
                              </Badge>
                            </div>
                          </div>
                        </CardHeader>
                        <CardContent className="flex items-end justify-between gap-3">
                          <p className="text-sm text-muted-foreground line-clamp-2 flex-1">{c.description}</p>
                          <Button
                            size="sm" variant="outline" className="shrink-0"
                            disabled={updatingContributionId === c.id}
                            onClick={() => handleUpdateContribution(c.id, s.next)}
                          >
                            {updatingContributionId === c.id
                              ? <Loader2 className="h-4 w-4 animate-spin" />
                              : s.nextLabel}
                          </Button>
                        </CardContent>
                      </Card>
                    );
                  })}
                </>
              )}
            </TabsContent>
          )}

          {/* ── Pestaña Bot (solo super-admin) ── */}
          {isSuperAdmin && (
            <TabsContent value="bot" className="mt-4 space-y-3">
              {(fetchingBotUsers || fetchingBotStats) ? (
                <div className="flex justify-center py-12">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : botUsers.length === 0 ? (
                <Card>
                  <CardContent className="py-12 text-center">
                    <Bot className="mx-auto mb-3 h-10 w-10 text-muted-foreground" />
                    <p className="font-medium">Sin usuarios aprobados</p>
                    <p className="text-sm text-muted-foreground">Aprobá usuarios primero desde la pestaña Pendientes.</p>
                  </CardContent>
                </Card>
              ) : (
                <>
                  <p className="text-sm text-muted-foreground">
                    {botUsers.length} usuario{botUsers.length !== 1 ? "s" : ""}
                  </p>
                  {botUsers.map((u) => {
                    const stats = botStats.find((s) => s.user_id === u.user_id);
                    return (
                      <Card key={u.user_id}>
                        <CardContent className="py-3 space-y-2.5">
                          {/* Fila 1: nombre + badges de uso */}
                          <div className="flex items-start justify-between gap-3 flex-wrap">
                            <div className="min-w-0">
                              <p className="font-medium text-sm truncate">{u.display_name}</p>
                              <p className="text-xs text-muted-foreground truncate">{u.email}</p>
                            </div>
                            {stats && (
                              <div className="flex flex-wrap gap-1.5 shrink-0">
                                <Badge variant="outline" className="text-xs">Hoy: {stats.messages_today}</Badge>
                                <Badge variant="outline" className="text-xs">Mes: {stats.messages_this_month}</Badge>
                                <Badge variant="secondary" className="text-xs font-mono">${stats.estimated_cost_usd.toFixed(4)}</Badge>
                              </div>
                            )}
                          </div>

                          {/* Fila 2: controles de canal */}
                          <div className="flex items-center gap-3 flex-wrap border-t pt-2.5">
                            {(["telegram", "whatsapp"] as const).map((channel) => {
                              const enabled = channel === "telegram" ? u.telegram_enabled : u.whatsapp_enabled;
                              const key = `${u.user_id}-${channel}`;
                              return (
                                <div key={channel} className="flex items-center gap-1.5">
                                  <span className="text-xs text-muted-foreground capitalize">{channel}:</span>
                                  <Badge
                                    variant={enabled === true ? "default" : "outline"}
                                    className={`text-xs ${enabled === true ? "bg-emerald-500 hover:bg-emerald-500 border-emerald-500" : ""}`}
                                  >
                                    {enabled === true ? "ON" : enabled === false ? "OFF" : "—"}
                                  </Badge>
                                  <Button
                                    size="sm"
                                    variant={enabled === true ? "destructive" : "outline"}
                                    className="h-7 text-xs px-2"
                                    disabled={togglingBotAccess === key}
                                    onClick={() => handleToggleBotAccess(u.user_id, u.display_name, channel, enabled)}
                                  >
                                    {togglingBotAccess === key
                                      ? <Loader2 className="h-3 w-3 animate-spin" />
                                      : enabled === true ? "Desactivar" : "Activar"
                                    }
                                  </Button>
                                </div>
                              );
                            })}
                          </div>

                          {/* Fila 3: tokens + cuota */}
                          {stats && (
                            <div className="border-t pt-2 space-y-1.5">
                              <div className="flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-muted-foreground">
                                <span>In: {fmtTokens(stats.tokens_input_month)} tok</span>
                                <span>Out: {fmtTokens(stats.tokens_output_month)} tok</span>
                                {stats.daily_limit != null && <span>Límite diario: {stats.daily_limit} msg</span>}
                                {stats.monthly_token_limit != null && <span>Límite mes: {fmtTokens(stats.monthly_token_limit)} tok</span>}
                                {stats.notes && <span className="italic">{stats.notes}</span>}
                              </div>
                              {editingQuotaId === u.user_id ? (
                                <div className="space-y-2 pt-1.5 border-t">
                                  <div className="grid grid-cols-2 gap-2">
                                    <div className="space-y-1">
                                      <Label className="text-xs">Límite diario (mensajes)</Label>
                                      <Input
                                        type="number" min="0" placeholder="Sin límite"
                                        value={quotaDailyLimit}
                                        onChange={(e) => setQuotaDailyLimit(e.target.value)}
                                        className="h-7 text-xs"
                                      />
                                    </div>
                                    <div className="space-y-1">
                                      <Label className="text-xs">Límite mensual (tokens)</Label>
                                      <Input
                                        type="number" min="0" placeholder="Sin límite"
                                        value={quotaMonthlyLimit}
                                        onChange={(e) => setQuotaMonthlyLimit(e.target.value)}
                                        className="h-7 text-xs"
                                      />
                                    </div>
                                  </div>
                                  <div className="space-y-1">
                                    <Label className="text-xs">Notas</Label>
                                    <Input
                                      placeholder="Ej: cliente premium"
                                      value={quotaNotes}
                                      onChange={(e) => setQuotaNotes(e.target.value)}
                                      className="h-7 text-xs"
                                    />
                                  </div>
                                  <div className="flex gap-2">
                                    <Button
                                      size="sm" className="h-7 text-xs"
                                      disabled={savingQuota}
                                      onClick={() => handleSetQuota(u.user_id)}
                                    >
                                      {savingQuota ? <Loader2 className="h-3 w-3 animate-spin" /> : "Guardar"}
                                    </Button>
                                    <Button
                                      size="sm" variant="ghost" className="h-7 text-xs"
                                      onClick={() => setEditingQuotaId(null)}
                                    >
                                      Cancelar
                                    </Button>
                                  </div>
                                </div>
                              ) : (
                                <Button
                                  size="sm" variant="ghost" className="h-7 text-xs -ml-1"
                                  onClick={() => {
                                    setEditingQuotaId(u.user_id);
                                    setQuotaDailyLimit(stats.daily_limit != null ? String(stats.daily_limit) : "");
                                    setQuotaMonthlyLimit(stats.monthly_token_limit != null ? String(stats.monthly_token_limit) : "");
                                    setQuotaNotes(stats.notes ?? "");
                                  }}
                                >
                                  Editar cuota
                                </Button>
                              )}
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    );
                  })}
                </>
              )}
            </TabsContent>
          )}

          {/* ── Pestaña Modo Admin (solo super-admin) ── */}
          {isSuperAdmin && (
            <TabsContent value="admins" className="mt-4 space-y-3">
              {fetchingApproved ? (
                <div className="flex justify-center py-12">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : approvedUsers.length === 0 ? (
                <Card>
                  <CardContent className="py-12 text-center">
                    <Users className="mx-auto mb-3 h-10 w-10 text-muted-foreground" />
                    <p className="font-medium">Sin usuarios aprobados</p>
                    <p className="text-sm text-muted-foreground">Aprobá usuarios primero desde la pestaña Pendientes.</p>
                  </CardContent>
                </Card>
              ) : (
                <>
                  <p className="text-sm text-muted-foreground">
                    {approvedUsers.length} usuario{approvedUsers.length !== 1 ? "s" : ""} aprobado{approvedUsers.length !== 1 ? "s" : ""}
                  </p>
                  {approvedUsers.map((u) => (
                    <Card key={u.id}>
                      <CardContent className="py-3 flex items-center justify-between gap-3">
                        <div className="min-w-0">
                          <p className="font-medium text-sm truncate">{u.display_name}</p>
                          <p className="text-xs text-muted-foreground truncate">{u.email}</p>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          {u.is_admin && (
                            <Badge variant="secondary" className="text-xs gap-1">
                              <ShieldCheck className="h-3 w-3" />
                              Admin
                            </Badge>
                          )}
                          <Button
                            size="sm"
                            variant={u.is_admin ? "destructive" : "outline"}
                            onClick={() => handleToggleAdmin(u.id, u.display_name, u.is_admin)}
                            disabled={togglingId === u.id}
                          >
                            {togglingId === u.id ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : u.is_admin ? (
                              <><ShieldOff className="mr-1.5 h-4 w-4" />Quitar admin</>
                            ) : (
                              <><ShieldCheck className="mr-1.5 h-4 w-4" />Hacer admin</>
                            )}
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </>
              )}
            </TabsContent>
          )}
        </Tabs>
      </div>

      <AlertDialog open={!!userToReject} onOpenChange={(open) => { if (!open) setUserToReject(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Rechazar a {userToReject?.display_name}?</AlertDialogTitle>
            <AlertDialogDescription>
              Se eliminará por completo su cuenta, su hogar y sus categorías iniciales.
              Esta acción es <strong>irreversible</strong> y libera su email para registrarse de nuevo.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={rejectingId === userToReject?.id}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={rejectingId === userToReject?.id}
              onClick={(e) => {
                e.preventDefault();
                if (userToReject) handleReject(userToReject.id, userToReject.display_name);
              }}
            >
              {rejectingId === userToReject?.id
                ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Eliminando...</>
                : "Sí, rechazar y eliminar"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
